import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    Typography
} from "@mui/material/";
import { useAssetTypeIdentifiers } from "../../hooks";
import { strings } from "../../i18n";
import { format, helpers } from "../../lib";
import actions from "../../../Store/actions";
import Attributes from "./Attributes";

import makeStyles from "@mui/styles/makeStyles";
import DatePicker from "react-datepicker";
import styles from "../styles";

import DropDownSearch from "./dropDownSearch";
import CheckBox from "./checkBox";
import useCombineBranchLocations from "./useCombineBranchLocation";
import datePickerStyles from "../../css/datePickerStyles";
import LoadingButton from "@mui/lab/LoadingButton";
import { logToSentry } from "../../../config/Sentry";
/**
 * Asset Details Component
 */

export default ({ onSubmit }) => {
    const { assets, typesList, branches, locations } = useSelector(
        (state) => state
    );
    const dispatch = useDispatch();
    const calStyles = makeStyles(datePickerStyles)();
    const classes = makeStyles(styles)();

    const {
        isExpected,
        isLive,
        branch,
        location,
        soldUpload,
        upload,
        attributes,
        asset,
        createdDate
    } = assets.item;

    const { identifier1, identifier2, identifier3, identifier4, make, type } =
        asset;

    const identifiers = useAssetTypeIdentifiers(assets.item.asset.type);

    const { datePickerConfig } = helpers;

    const [date, setDate] = useState({
        startDate: new Date(),
        endDate: new Date()
    });
    const [loading, setLoading] = useState(false);
    const [currentBranch, setCurrentBranch] = useState("");
    const [currentLocation, setCurrentLocation] = useState("");
    const [locationsList, setLocationsList] = useState([]);
    const [disableSave, setDisableSave] = useState(false);

    const handleCheckboxChange = (name: string) => (event: Event) =>
        dispatch(
            actions.assets.create.itemUpdate({ [name]: event.target.checked })
        );

    const handleDateChange = (name: string, date: Date) =>
        dispatch(
            actions.assets.create.itemUpdate({
                [name]: format.dateTimeNew(date)
            })
        );

    const getCurrentBranch = (name) =>
        branches.data.find((item) => item.name === name);
    const getCurrentLocation = (name) =>
        locations.data.find((item) => item.name === name);

    const onSave = async () => {
        try {
            setLoading(true);
            const { soldUpload, upload } = assets.item;
            const branchId = getCurrentBranch(currentBranch).id;
            const locationId = getCurrentLocation(currentLocation).id;
            const next = {
                ...assets.item,
                asset: assets.item.asset.id,
                branch: branchId,
                category: assets.item.category.id,
                location: locationId,
                excludeFrom: date.startDate,
                excludeUntil: date.endDate,
                soldUpload: soldUpload !== null ? soldUpload.id : "",
                upload: upload !== null ? upload.id : ""
            };
            await dispatch(actions.assets.create.patchRequest(next));
            dispatch(
                actions.toast.create.success(
                    strings("assetUpdatedSuccessfully")
                )
            );
            // await dispatch(actions.assets.create.getRequest());
            onSubmit();
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On updating assets:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onUpdatingAssets"))
            );
        }
        setLoading(false);
    };

    /** Get branch and Locatiion */
    useEffect(() => {
        /*** Current Branch & Location from selected assaet  */
        const getBranch = helpers.getListItemForId(branches, branch);
        const getLocation = helpers.getListItemForId(locations, location);

        setCurrentBranch(getBranch.name);
        setCurrentLocation(getLocation.name);
        setDisableSave(
            getLocation && getLocation.name.length > 1 ? false : true
        );
    }, [helpers, branches, branch]);

    /** Branches should be selcted  */
    const branchesList = useMemo(() => {
        return branches.data.map((item) => item.name);
    }, [branches]);

    /** On branch change */
    const onBranchChange = (value) => {
        try {
            setCurrentBranch(value);
            setCurrentLocation("");
            setLocationsList([]);
            setDisableSave(true);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On updating branch:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onUpdatingBranch"))
            );
        }
    };

    /** Combine branch and locations */

    const combineBranchLocations = useCombineBranchLocations();

    /** Wrapping in memo to set the state to avoid rerendering */
    const listCurrentLocations = () =>
        useMemo(() => {
            const locationsListName = combineBranchLocations.filter(
                (item) => item.branchName === currentBranch
            )[0];
            if (locationsListName && locationsListName.locations.length > 0)
                setLocationsList(
                    locationsListName.locations.map((item) => item.name)
                );
            else return [];
        }, [currentBranch, locations, combineBranchLocations]);
    listCurrentLocations();

    /** On location  change  for the particular branch */
    const onLocationChange = (value) => {
        try {
            setCurrentLocation(value);
            setDisableSave(value && value.length > 1 ? false : true);
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On updating location:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onUpdatingLocation"))
            );
        }
    };

    const makeTextField = (label: string, value: string) =>
        value && (
            <Grid item xs={6}>
                <FormControl margin="dense">
                    <Typography variant="caption">{label}</Typography>
                    <Typography variant="body2">{value}</Typography>
                </FormControl>
            </Grid>
        );

    // Calendar Container
    const CalContainer = ({ children }) => {
        return (
            <Box className={calStyles.customDatePickerStyle}>
                <div className={classes.datePickerContainer}>{children}</div>
            </Box>
        );
    };

    return (
        <form>
            <Grid container>
                {identifier1 &&
                    makeTextField(
                        strings(`${identifiers.identifier1}`),
                        identifier1
                    )}
                {identifier2 &&
                    makeTextField(
                        strings(`${identifiers.identifier2}`),
                        identifier2
                    )}
                {identifier3 &&
                    makeTextField(
                        strings(`${identifiers.identifier3}`),
                        identifier3
                    )}
                {identifier4 &&
                    makeTextField(
                        strings(`${identifiers.identifier4}`),
                        identifier4
                    )}
                <Divider />

                <Grid item xs={6}>
                    {makeTextField(
                        strings("make"),
                        helpers.getListItemForId(typesList.makes, make).name
                    )}
                </Grid>
                <Grid item xs={6}>
                    {makeTextField(
                        strings("type"),
                        helpers.getListItemForId(typesList.assetTypes, type)
                            .name
                    )}
                </Grid>
                <Grid item xs={12}>
                    <FormControl margin="normal" fullWidth>
                        <DropDownSearch
                            label={strings("branches")}
                            data={branchesList}
                            currentValue={currentBranch}
                            onChangeFn={onBranchChange}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <FormControl margin="normal" fullWidth>
                        <DropDownSearch
                            label={strings("locations")}
                            data={locationsList}
                            currentValue={currentLocation}
                            onChangeFn={onLocationChange}
                        />
                    </FormControl>
                </Grid>
                {attributes && attributes.length > 0 && (
                    <Attributes
                        assetTypeId={asset.type}
                        attributes={attributes}
                    />
                )}

                {upload ? (
                    <Grid container spacing={3}>
                        {makeTextField(
                            strings("createdDate"),
                            `${format.dateTimeNew(createdDate)}`
                        )}
                        {soldUpload && soldUpload.statusDate ? (
                            <>
                                {soldUpload.statusDate < upload.statusDate ? (
                                    <>
                                        {makeTextField(
                                            strings("lastUploadDate"),
                                            `${format.dateTimeNew(
                                                upload.statusDate
                                            )}`
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {makeTextField(
                                            strings("lastUploadDate"),
                                            `${format.dateTimeNew(
                                                soldUpload.statusDate
                                            )}`
                                        )}
                                    </>
                                )}
                                {soldUpload.originalFilename
                                    ? makeTextField(
                                          strings("lastUploadSource"),
                                          soldUpload.originalFilename
                                              .split("/")
                                              .pop()
                                      )
                                    : null}
                            </>
                        ) : (
                            <>
                                {upload ? (
                                    <>
                                        {makeTextField(
                                            strings("lastUploadDate"),
                                            `${format.dateTimeNew(
                                                upload.statusDate
                                            )}`
                                        )}
                                        {makeTextField(
                                            strings("lastUploadSource"),
                                            upload.originalFilename &&
                                                upload.originalFilename
                                                    .split("/")
                                                    .pop()
                                        )}
                                    </>
                                ) : null}
                            </>
                        )}
                    </Grid>
                ) : null}
            </Grid>
            <Grid container spacing={3}>
                <CheckBox
                    label={strings("isExpected")}
                    value={isExpected}
                    onChangeFn={handleCheckboxChange("isExpected")}
                />
                <CheckBox
                    label={strings("isLive")}
                    value={isLive}
                    onChangeFn={handleCheckboxChange("isLive")}
                />
            </Grid>
            <Divider />
            <FormControl fullWidth margin="normal">
                <Typography variant="caption">
                    {strings("excludeFrom")}
                </Typography>
                <DatePicker
                    popperContainer={CalContainer}
                    showTimeSelect
                    minDate={datePickerConfig.minDate}
                    filterTime={datePickerConfig.filterPassedTime}
                    placeholderText={strings("excludeFrom")}
                    timeIntervals={datePickerConfig.timeIntervals}
                    className={classes.datePickerStyle}
                    portalId="excludeFrom-portal"
                    dateFormat={datePickerConfig.format}
                    value={date.startDate}
                    selected={date.startDate}
                    //   //showTimeInput
                    onChange={(dt) => {
                        handleDateChange("excludeFrom", dt);
                        setDate({ ...date, startDate: dt });
                    }}
                />
            </FormControl>
            <FormControl fullWidth margin="normal">
                <Typography variant="caption">
                    {strings("excludeUntil")}
                </Typography>
                <DatePicker
                    popperContainer={CalContainer}
                    showTimeSelect
                    minDate={date.startDate}
                    filterTime={(time) => {
                        const currentDate = new Date(date.startDate);
                        const selectedDate = new Date(time);
                        return currentDate.getTime() < selectedDate.getTime();
                    }}
                    placeholderText={strings("excludeUntil")}
                    timeIntervals={datePickerConfig.timeIntervals}
                    className={classes.datePickerStyle}
                    portalId="excludeUntil-portal"
                    dateFormat={datePickerConfig.format}
                    value={
                        date.endDate < date.startDate
                            ? date.startDate
                            : date.endDate
                    }
                    selected={
                        date.endDate < date.startDate
                            ? date.startDate
                            : date.endDate
                    }
                    startDate={date.endDate}
                    //showTimeInput
                    onChange={(dt) => {
                        handleDateChange("excludeUntil", dt);
                        setDate({ ...date, endDate: dt });
                    }}
                />
            </FormControl>
            <FormControl fullWidth margin="normal" variant="standard">
                <LoadingButton
                    onClick={onSave}
                    variant="outlined"
                    disabled={disableSave}
                    size="large"
                    loading={loading}
                    loadingIndicator={<CircularProgress />}
                >
                    {strings("save")}
                </LoadingButton>
            </FormControl>
        </form>
    );
};
